import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { DataRecord, GoogleLocal } from '../../../types';
import { useIsMobile } from '../mobile/MobileChrome';
import { RatingCardMobile } from '../mobile/RatingCardMobile';
import { LocalesMobile } from '../mobile/LocalesMobile';
import { KpiRail, type MobileKpi } from '../mobile/MobileCards';
import { MonthlyChart, MonthlyLegend, StarSplit, type SandboxTheme } from './SandboxResumen';
import {
  cleanReviewText,
  fmtDay,
  fmtDecimal,
  fmtInt,
  isCriticalReview,
  localInsights,
  localKey,
  localesData,
  localesInsight,
  localesSummary,
  ratedGoogle,
  str,
  topicRows,
  type BandejaFocus,
  type LocalRow,
  type LocalStatus,
  type ToneCount,
} from './resumenData';

/**
 * Vista de Locales: compara las fichas de Google del cliente y abre el detalle de cada una.
 * Usa los mismos datos y el mismo criterio de críticos sin responder que el Resumen y la Bandeja.
 * Con menos de 5 reseñas en el período la nota no se considera concluyente.
 */

interface Props {
  theme: SandboxTheme;
  /** Comentarios del período activo, sin respuestas propias. */
  rangeComments: DataRecord[];
  /** Historial completo disponible. */
  allComments: DataRecord[];
  /** Todas las fichas de Google del cliente, con o sin reseñas. */
  locales?: GoogleLocal[];
  /** Período anterior de igual largo. null si no hay. */
  prevComments: DataRecord[] | null;
  periodLabel: string;
  prevLabel: string;
  /** Críticos sin responder en todos los canales, tal como los cuenta el Resumen. */
  urgentCount: number;
  showHeader?: boolean;
  onOpenBandeja: (focus?: BandejaFocus) => void;
  onNavigateToResumen: () => void;
  onNavigateSection?: (sectionId: string) => void;
  onExportPdf?: () => void;
  /** Local que se abre de entrada en su detalle, por ejemplo al llegar desde una tarjeta del Resumen. */
  initialLocalId?: string | null;
  /** Abre la vista Temas. Si no viene, el menú baja a la sección de Temas del Resumen. */
  onNavigateToTemas?: () => void;
}

type DeltaTone = 'good' | 'bad' | 'flat';

const TAG_CLASS: Record<LocalStatus, string> = {
  attention: 'bad',
  review: 'bad',
  improving: 'good',
  holds: 'good',
  stable: 'stable',
  none: 'stable',
};

const GENERIC_WORDS = new Set(['centro', 'clínica', 'clinica', 'hospital']);

/** Nombre corto de cada local: quita las palabras que comparten todos y las genéricas (Centro, Clínica, Hospital). */
function shortNames(rows: LocalRow[]): Map<string, string> {
  const split = rows.map((r) => r.name.split(' ').filter(Boolean));
  const common = new Set<string>();
  if (split.length > 1) {
    split[0].forEach((w) => common.add(w.toLowerCase()));
    split.slice(1).forEach((words) => {
      const here = new Set(words.map((w) => w.toLowerCase()));
      common.forEach((w) => {
        if (!here.has(w)) common.delete(w);
      });
    });
  }
  const out = new Map<string, string>();
  rows.forEach((r, i) => {
    const kept = split[i].filter((w) => !common.has(w.toLowerCase()) && !GENERIC_WORDS.has(w.toLowerCase()));
    out.set(r.id, kept.length > 0 ? kept.join(' ') : r.name);
  });
  return out;
}

/** Cambio de la nota contra el período anterior. Con menos de 5 reseñas en alguno de los dos queda en gris. */
function avgDelta(
  cur: number | null,
  prev: number | null,
  curN: number,
  prevN: number | null,
): { text: string; tone: DeltaTone } {
  if (cur === null) return { text: 'Sin reseñas en el período', tone: 'flat' };
  if (prevN === null) return { text: 'Sin período anterior para comparar', tone: 'flat' };
  if (prev === null) return { text: 'Sin reseñas en el período anterior', tone: 'flat' };
  const d = cur - prev;
  if (Math.abs(d) < 0.05) return { text: `Igual que el período anterior (${fmtDecimal(prev)})`, tone: 'flat' };
  const small = curN < 5 || prevN < 5;
  return {
    text: `${d > 0 ? '▲' : '▼'} ${fmtDecimal(Math.abs(d))} vs ${fmtDecimal(prev)} del período anterior${
      small ? ' (pocas reseñas)' : ''
    }`,
    tone: small ? 'flat' : d > 0 ? 'good' : 'bad',
  };
}

function daysSince(iso: string): number {
  const t = new Date(`${iso.slice(0, 10)}T12:00:00Z`).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.round((Date.now() - t) / 86400000));
}

function agoText(days: number): string {
  if (days === 0) return 'Hoy';
  return `Hace ${days} ${days === 1 ? 'día' : 'días'}`;
}

function Sect({
  id,
  tag,
  title,
  subtitle,
  children,
}: {
  id?: string;
  tag: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className='sbx-section'>
      <div className='sbx-section-head'>
        <span className='sbx-tag'>{tag}</span>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function ToneBar({ tone }: { tone: ToneCount }) {
  const classified = tone.pos + tone.neu + tone.neg;
  const w = (n: number) => `${classified > 0 ? ((n / classified) * 100).toFixed(1) : 0}%`;
  return (
    <div
      className='sbx-tonebar'
      style={{ height: 12 }}
      role='img'
      aria-label={`${tone.pos} positivas, ${tone.neu} neutras, ${tone.neg} negativas`}
    >
      <i className='pos' style={{ width: w(tone.pos) }} />
      <i className='neu' style={{ width: w(tone.neu) }} />
      <i className='neg' style={{ width: w(tone.neg) }} />
    </div>
  );
}

/** Una fila por local: punto gris con la nota acumulada y punto de color con la nota del período. */
function Dumbbell({ rows, names }: { rows: LocalRow[]; names: Map<string, string> }) {
  const vals: number[] = [];
  rows.forEach((r) => {
    if (r.avg !== null) vals.push(r.avg);
    if (r.curAvg !== null) vals.push(r.curAvg);
  });
  if (vals.length === 0) return null;
  const lo = Math.min(3, Math.floor(Math.min(...vals)));
  const rowH = 46;
  const left = 200;
  const right = 600;
  const top = 30;
  const height = top + rows.length * rowH + 28;
  const xOf = (v: number) => left + ((v - lo) / (5 - lo)) * (right - left);
  const ticks: number[] = [];
  for (let v = lo; v <= 5; v += 1) ticks.push(v);
  const clip = (s: string) => (s.length > 28 ? `${s.slice(0, 27)}…` : s);
  return (
    <svg
      className='sbx-svg sbx-dumb'
      viewBox={`0 0 780 ${height}`}
      width='100%'
      role='img'
      aria-label='Nota acumulada y nota del período de cada local'
    >
      {ticks.map((v) => (
        <g key={v}>
          <line className='grid' x1={xOf(v)} x2={xOf(v)} y1={top - 8} y2={height - 24} />
          <text x={xOf(v)} y={height - 8} textAnchor='middle'>
            {fmtDecimal(v)}
          </text>
        </g>
      ))}
      <line className='meta' x1={xOf(4.3)} x2={xOf(4.3)} y1={top - 8} y2={height - 24} />
      <text x={xOf(4.3)} y={14} textAnchor='middle'>
        Meta 4,3
      </text>
      {rows.map((r, i) => {
        const y = top + i * rowH + rowH / 2;
        return (
          <g key={r.id}>
            <text className='strong' x={0} y={y + 4}>
              {clip(names.get(r.id) ?? r.name)}
            </text>
            {r.avg !== null && r.curAvg !== null && (
              <line className='link' x1={xOf(r.avg)} x2={xOf(r.curAvg)} y1={y} y2={y} />
            )}
            {r.avg !== null && <circle className='d-all' cx={xOf(r.avg)} cy={y} r={6} />}
            {r.curAvg !== null && (
              <circle
                className={`d-cur${r.curAvg < 4 ? ' bad' : ''}${r.small ? ' open' : ''}`}
                cx={xOf(r.curAvg)}
                cy={y}
                r={7}
              />
            )}
            <text x={620} y={y - 3}>
              {r.avg !== null ? `${fmtDecimal(r.avg)} acumulada` : 'Sin historial'}
            </text>
            <text className='strong' x={620} y={y + 12}>
              {r.curAvg !== null ? `${fmtDecimal(r.curAvg)} en el período (${r.curN})` : 'Sin reseñas en el período'}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LocalDetail({
  row,
  periodLabel,
  prevLabel,
  rangeComments,
  allComments,
  onBack,
  onOpenBandeja,
}: {
  row: LocalRow;
  periodLabel: string;
  prevLabel: string;
  rangeComments: DataRecord[];
  allComments: DataRecord[];
  onBack: () => void;
  onOpenBandeja: (focus?: BandejaFocus) => void;
}) {
  // Temas de los últimos 6 meses de esta ficha: un local con pocas reseñas al mes no da un patrón en 28 días.
  const topics = useMemo(() => {
    const cutoff = new Date(Date.now() - 183 * 86400000).toISOString().slice(0, 10);
    const mine = ratedGoogle(allComments).filter(
      (c) => localKey(c) === row.id && str(c.date).slice(0, 10) >= cutoff,
    );
    return topicRows(mine, null, 8);
  }, [allComments, row.id]);
  const insights = useMemo(() => localInsights(row, topics), [row, topics]);
  const critical = useMemo(
    () =>
      ratedGoogle(rangeComments)
        .filter((c) => localKey(c) === row.id && isCriticalReview(c))
        .sort((a, b) => str(a.date).localeCompare(str(b.date))),
    [rangeComments, row.id],
  );
  const delta = avgDelta(row.curAvg, row.prevAvg, row.curN, row.prevN);
  const hasMonthly = row.months.some((m) => m.avg !== null);
  const isMobile = useIsMobile();

  return (
    <>
      <nav className='sbx-crumb' aria-label='Ruta'>
        <button type='button' onClick={onBack}>
          Locales
        </button>
        <span aria-hidden='true'>/</span>
        <strong>{row.name}</strong>
      </nav>

      <section className='sbx-head'>
        <div className='sbx-head-title'>
          <h1>{row.name}</h1>
          <p>
            {periodLabel}
            {prevLabel ? ` · comparado con ${prevLabel}` : ''}
          </p>
        </div>
        <div className='sbx-head-controls'>
          <span className={`sbx-loc-tag ${TAG_CLASS[row.status]}`}>{row.label}</span>
          {row.mapsUrl && (
            <a className='sbx-link' href={row.mapsUrl} target='_blank' rel='noopener noreferrer'>
              Ver en Google Maps
            </a>
          )}
        </div>
      </section>

      {insights.length > 0 && (
        <section className='sbx-gert' aria-label='Lectura de Gertrudis'>
          <div className='sbx-gert-grid'>
            {insights.map((i) => (
              <div key={i.id} className='sbx-gert-card'>
                <span className={`sbx-gert-label ${i.tone}`}>{i.label}</span>
                <p>
                  {i.segs.map((s, idx) =>
                    s.bold ? <strong key={idx}>{s.text}</strong> : <span key={idx}>{s.text}</span>,
                  )}
                </p>
                {i.id === 'alert' && row.critOpen > 0 && (
                  <button type='button' onClick={() => onOpenBandeja({ location: row.name })}>
                    Ver los críticos en la Bandeja
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {isMobile ? (
        <KpiRail
          kpis={[
            {
              id: 'acumulada',
              label: 'Nota acumulada',
              value: row.avg !== null ? fmtDecimal(row.avg) : '-',
              unit: 'de 5',
              sub: `${fmtInt(row.stars.total)} reseñas en todo el historial`,
            },
            {
              id: 'periodo',
              label: 'Nota del período',
              value: row.curAvg !== null ? fmtDecimal(row.curAvg) : '-',
              unit: row.curAvg !== null ? 'de 5' : undefined,
              delta: delta,
              sub: row.small && row.curN > 0 ? 'Menos de 5 reseñas: no es concluyente' : undefined,
            },
            {
              id: 'resenas',
              label: 'Reseñas del período',
              value: fmtInt(row.curN),
              unit: `${row.curLow} de 1 a 2★`,
              sub: row.prevN === null ? undefined : `${fmtInt(row.prevN)} en el período anterior`,
            },
            {
              id: 'criticos',
              label: 'Críticos sin responder',
              value: String(row.critOpen),
              unit: row.critOpen > 0 ? 'requieren atención' : undefined,
              critical: row.critOpen > 0,
              cta: row.critOpen > 0 ? { label: 'Atender ahora', onClick: () => onOpenBandeja({ location: row.name }) } : undefined,
            },
          ]}
        />
      ) : (
      <section aria-label='Indicadores del local'>
        <div className='sbx-grid-kpi'>
          <div className='sbx-card sbx-kpi'>
            <span className='sbx-kpi-label'>Nota acumulada</span>
            <div className='sbx-kpi-value'>
              <strong>{row.avg !== null ? fmtDecimal(row.avg) : '-'}</strong>
              <span>de 5</span>
            </div>
            <span className='sbx-kpi-sub'>{fmtInt(row.stars.total)} reseñas en todo el historial</span>
          </div>
          <div className='sbx-card sbx-kpi'>
            <span className='sbx-kpi-label'>Nota del período</span>
            <div className='sbx-kpi-value'>
              <strong>{row.curAvg !== null ? fmtDecimal(row.curAvg) : '-'}</strong>
              {row.curAvg !== null && <span>de 5</span>}
            </div>
            <span className={`sbx-kpi-delta is-${delta.tone}`}>{delta.text}</span>
            {row.small && row.curN > 0 && <span className='sbx-kpi-sub'>Menos de 5 reseñas: no es concluyente</span>}
          </div>
          <div className='sbx-card sbx-kpi'>
            <span className='sbx-kpi-label'>Reseñas del período</span>
            <div className='sbx-kpi-value'>
              <strong>{fmtInt(row.curN)}</strong>
              <span>{row.curLow} de 1 a 2★</span>
            </div>
            <span className='sbx-kpi-delta is-flat'>
              {row.prevN === null ? 'Sin período anterior para comparar' : `${fmtInt(row.prevN)} en el período anterior`}
            </span>
          </div>
          <div className={`sbx-card sbx-kpi${row.critOpen > 0 ? ' is-critical' : ''}`}>
            <span className='sbx-kpi-label'>Críticos sin responder</span>
            <div className='sbx-kpi-value'>
              <strong>{row.critOpen}</strong>
              {row.critOpen > 0 && <span>requieren atención</span>}
            </div>
            <span className='sbx-kpi-sub'>Meta de respuesta 24 h</span>
            {row.critOpen > 0 && (
              <button type='button' className='sbx-kpi-cta' onClick={() => onOpenBandeja({ location: row.name })}>
                Atender ahora
              </button>
            )}
          </div>
        </div>
      </section>
      )}

      <section className='sbx-row' aria-label='Evolución de la nota de este local'>
        {isMobile ? (
          <RatingCardMobile
            title='Nota mensual en Google, 12 meses'
            months={row.months}
            stars={row.stars}
            starsCaption={`${fmtInt(row.stars.total)} reseñas en todo el historial`}
          />
        ) : (
          <div className='sbx-card sbx-card-wide'>
            {hasMonthly && (
              <>
                <div>
                  <h3>Nota mensual en Google, 12 meses</h3>
                  <p>Barras grises: reseñas recibidas cada mes. El mes con asterisco está en curso.</p>
                </div>
                <MonthlyLegend pts={row.months} />
                <MonthlyChart pts={row.months} />
              </>
            )}
            <StarSplit stars={row.stars} caption={`${fmtInt(row.stars.total)} reseñas en todo el historial de este local`} />
          </div>
        )}
      </section>

      {topics.rows.length > 0 && (
        <Sect
          tag='Temas'
          title='De qué hablan las reseñas de este local'
          subtitle='Últimos 6 meses. Toca un tema para ver sus reseñas en la Bandeja.'
        >
          <div className='sbx-card'>
            <div className='sbx-topic-list'>
              {topics.rows.map((t) => (
                <button
                  key={t.key}
                  type='button'
                  className='sbx-topic-row'
                  onClick={() => onOpenBandeja({ location: row.name, topic: t.key })}
                  aria-label={`Ver en la Bandeja las reseñas de ${t.name}`}
                >
                  <span className='name'>{t.name}</span>
                  <span className='n'>
                    {fmtInt(t.total)} {t.total === 1 ? 'mención' : 'menciones'}
                  </span>
                  <ToneBar tone={t.tone} />
                  <span className='tone'>
                    {t.tone.pos} positivas · {t.tone.neg} negativas
                  </span>
                </button>
              ))}
            </div>
            <p className='sbx-table-note'>
              {fmtInt(topics.assigned)} de {fmtInt(topics.total)} reseñas de los últimos 6 meses tienen un tema asignado.
              La Bandeja muestra las reseñas del período elegido arriba.
            </p>
          </div>
        </Sect>
      )}

      <Sect tag='Bandeja' title='Reseñas críticas sin responder' subtitle='Las más antiguas primero.'>
        {critical.length === 0 ? (
          <div className='sbx-empty'>No hay reseñas críticas sin responder en este local durante el período.</div>
        ) : (
          <div className='sbx-grid-cards'>
            {critical.map((c) => {
              const stars = Number(c.star_rating);
              const raw = cleanReviewText(c.message) || 'Sin texto';
              const text = raw.length > 320 ? `${raw.slice(0, 317).trimEnd()}...` : raw;
              return (
                <article
                  key={str(c.feedback_id) || `${str(c.author_name)}|${str(c.date)}`}
                  className='sbx-card sbx-crit-card'
                >
                  <div className='sbx-card-top'>
                    <strong>{str(c.author_name).trim() || 'Cliente'}</strong>
                    {stars > 0 && <span>★ {stars} de 5</span>}
                  </div>
                  <blockquote>{text}</blockquote>
                  <div className='sbx-card-top'>
                    <span>
                      {fmtDay(str(c.date))} · {agoText(daysSince(str(c.date)))}
                    </span>
                  </div>
                  <button
                    type='button'
                    className='sbx-link'
                    onClick={() => onOpenBandeja({ location: row.name, feedbackId: str(c.feedback_id) })}
                  >
                    Responder en la Bandeja
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </Sect>
    </>
  );
}

export function SandboxLocales({
  theme,
  rangeComments,
  allComments,
  locales = [],
  prevComments,
  periodLabel,
  prevLabel,
  urgentCount,
  showHeader = true,
  onOpenBandeja,
  onNavigateToResumen,
  onNavigateSection,
  onExportPdf,
  initialLocalId = null,
  onNavigateToTemas,
}: Props) {
  const isMobile = useIsMobile();
  const rootRef = useRef<HTMLDivElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(initialLocalId);

  const rows = useMemo(
    () => localesData(allComments, rangeComments, prevComments, locales),
    [allComments, rangeComments, prevComments, locales],
  );
  const summary = useMemo(() => localesSummary(rows), [rows]);
  const insight = useMemo(() => localesInsight(rows, summary.critOpen), [rows, summary.critOpen]);
  const names = useMemo(() => shortNames(rows), [rows]);
  const selected = selectedId ? (rows.find((r) => r.id === selectedId) ?? null) : null;

  // Al entrar a un local o volver al comparativo, la vista parte desde arriba.
  useEffect(() => {
    rootRef.current?.scrollIntoView({ block: 'start' });
  }, [selectedId]);

  const goSection = (id: string) => {
    if (onNavigateSection) onNavigateSection(id);
    else onNavigateToResumen();
  };

  const header = showHeader ? (
    <header className='sbx-top-bar'>
      <div className='sbx-top-bar-inner'>
        <div className='sbx-brand'>
          <span className='sbx-brand-title'>Social Wolf</span>
        </div>
        <nav className='sbx-nav' aria-label='Secciones'>
          <button type='button' className='sbx-nav-link' onClick={onNavigateToResumen}>
            Resumen
          </button>
          <button type='button' className='sbx-nav-link' onClick={() => onOpenBandeja()}>
            Bandeja {urgentCount > 0 && <span className='sbx-nav-badge'>{urgentCount}</span>}
          </button>
          <button type='button' className='sbx-nav-link' onClick={() => goSection('canales')}>
            Canales
          </button>
          <button type='button' className='sbx-nav-link' onClick={() => (onNavigateToTemas ? onNavigateToTemas() : goSection('temas'))}>
            Temas
          </button>
          <button type='button' className='sbx-nav-link active' onClick={() => setSelectedId(null)}>
            Locales
          </button>
          <button type='button' className='sbx-nav-link' onClick={() => goSection('plan')}>
            Plan
          </button>
        </nav>
        {onExportPdf && (
          <div className='sbx-top-right'>
            <button type='button' className='sbx-btn-pdf' onClick={onExportPdf}>
              Exportar PDF
            </button>
          </div>
        )}
      </div>
    </header>
  ) : null;

  if (selected) {
    return (
      <div className='sbx' data-theme={theme} ref={rootRef}>
        {header}
        <LocalDetail
          row={selected}
          periodLabel={periodLabel}
          prevLabel={prevLabel}
          rangeComments={rangeComments}
          allComments={allComments}
          onBack={() => setSelectedId(null)}
          onOpenBandeja={onOpenBandeja}
        />
      </div>
    );
  }

  const notaDelta = avgDelta(summary.curAvg, summary.prevAvg, summary.curN, summary.prevN);

  if (isMobile && rows.length > 0) {
    const kpis: MobileKpi[] = [
      {
        id: 'acumulada',
        label: 'Nota acumulada de la marca',
        value: summary.avg !== null ? fmtDecimal(summary.avg) : '-',
        unit: 'de 5',
        sub: `${fmtInt(summary.total)} reseñas en ${rows.length} locales`,
      },
      {
        id: 'periodo',
        label: 'Nota del período',
        value: summary.curAvg !== null ? fmtDecimal(summary.curAvg) : '-',
        unit: summary.curAvg !== null ? 'de 5' : undefined,
        delta: notaDelta,
      },
      {
        id: 'nuevas',
        label: 'Reseñas nuevas',
        value: fmtInt(summary.curN),
        sub: summary.prevN === null ? undefined : `${fmtInt(summary.prevN)} en el período anterior`,
      },
      {
        id: 'criticos',
        label: 'Críticos sin responder',
        value: String(summary.critOpen),
        unit: summary.critOpen > 0 ? `en ${summary.critLocals} ${summary.critLocals === 1 ? 'local' : 'locales'}` : undefined,
        critical: summary.critOpen > 0,
        cta: summary.critOpen > 0 ? { label: 'Atender ahora', onClick: () => onOpenBandeja() } : undefined,
      },
    ];
    return (
      <div className='sbx' data-theme={theme} ref={rootRef}>
        <LocalesMobile
          kpis={kpis}
          locals={rows.map((r) => ({
            id: r.id,
            name: r.name,
            tag: { label: r.label, cls: TAG_CLASS[r.status] },
            attention: r.status === 'attention',
            avg: r.avg,
            starsTotal: r.stars.total,
            curAvg: r.curAvg,
            curN: r.curN,
            critOpen: r.critOpen,
            delta: avgDelta(r.curAvg, r.prevAvg, r.curN, r.prevN),
          }))}
          onOpen={setSelectedId}
        />
      </div>
    );
  }

  return (
    <div className='sbx' data-theme={theme} ref={rootRef}>
      {header}

      <section className='sbx-head'>
        <div className='sbx-head-title'>
          <h1>Locales</h1>
          <p>
            {periodLabel}
            {prevLabel ? ` · comparado con ${prevLabel}` : ''}. Solo reseñas de Google: Instagram y Facebook no están
            asociados a un local.
          </p>
        </div>
      </section>

      {rows.length === 0 ? (
        <div className='sbx-empty'>No hay reseñas de Google por local para mostrar.</div>
      ) : (
        <>
          {insight && (
            <section className='sbx-gert' aria-label='Lectura de Gertrudis'>
              <div className='sbx-gert-grid'>
                <div className='sbx-gert-card'>
                  <span className='sbx-gert-label alert'>Dónde se concentra</span>
                  <p>
                    {insight.map((s, idx) =>
                      s.bold ? <strong key={idx}>{s.text}</strong> : <span key={idx}>{s.text}</span>,
                    )}
                  </p>
                  {summary.critOpen > 0 && (
                    <button type='button' onClick={() => onOpenBandeja()}>
                      Ver los críticos en la Bandeja
                    </button>
                  )}
                </div>
              </div>
            </section>
          )}

          <section aria-label='Indicadores de los locales'>
            <div className='sbx-grid-kpi'>
              <div className='sbx-card sbx-kpi'>
                <span className='sbx-kpi-label'>Nota acumulada de la marca</span>
                <div className='sbx-kpi-value'>
                  <strong>{summary.avg !== null ? fmtDecimal(summary.avg) : '-'}</strong>
                  <span>de 5</span>
                </div>
                <span className='sbx-kpi-sub'>
                  {fmtInt(summary.total)} reseñas en {rows.length} locales
                </span>
              </div>
              <div className='sbx-card sbx-kpi'>
                <span className='sbx-kpi-label'>Nota del período</span>
                <div className='sbx-kpi-value'>
                  <strong>{summary.curAvg !== null ? fmtDecimal(summary.curAvg) : '-'}</strong>
                  {summary.curAvg !== null && <span>de 5</span>}
                </div>
                <span className={`sbx-kpi-delta is-${notaDelta.tone}`}>{notaDelta.text}</span>
              </div>
              <div className='sbx-card sbx-kpi'>
                <span className='sbx-kpi-label'>Reseñas nuevas</span>
                <div className='sbx-kpi-value'>
                  <strong>{fmtInt(summary.curN)}</strong>
                </div>
                <span className='sbx-kpi-delta is-flat'>
                  {summary.prevN === null
                    ? 'Sin período anterior para comparar'
                    : `${fmtInt(summary.prevN)} en el período anterior`}
                </span>
              </div>
              <div className={`sbx-card sbx-kpi${summary.critOpen > 0 ? ' is-critical' : ''}`}>
                <span className='sbx-kpi-label'>Críticos sin responder</span>
                <div className='sbx-kpi-value'>
                  <strong>{summary.critOpen}</strong>
                  {summary.critOpen > 0 && (
                    <span>
                      en {summary.critLocals} {summary.critLocals === 1 ? 'local' : 'locales'}
                    </span>
                  )}
                </div>
                <span className='sbx-kpi-sub'>
                  Solo Google{urgentCount !== summary.critOpen ? `. En todos los canales: ${urgentCount}` : ''}
                </span>
                {summary.critOpen > 0 && (
                  <button type='button' className='sbx-kpi-cta' onClick={() => onOpenBandeja()}>
                    Atender ahora
                  </button>
                )}
              </div>
            </div>
          </section>

          {!isMobile && (
            <div className='sbx-card'>
              <div>
                <h3>Nota acumulada y nota del período</h3>
                <p>
                  El punto gris es la nota de todo el historial. El punto de color es la nota del período, y va abierto
                  cuando hay menos de 5 reseñas.
                </p>
              </div>
              <Dumbbell rows={rows} names={names} />
            </div>
          )}

          <Sect id='locales' tag='Locales' title='Cómo está cada local'>
            <div className='sbx-grid-cards'>
              {rows.map((r) => {
                const d = avgDelta(r.curAvg, r.prevAvg, r.curN, r.prevN);
                return (
                  <div
                    key={r.id}
                    className='sbx-card sbx-loc-card'
                    style={r.status === 'attention' ? { borderColor: 'var(--sbx-crit)' } : undefined}
                  >
                    <div className='sbx-card-top' style={{ alignItems: 'center' }}>
                      <strong>{r.name}</strong>
                      <span className={`sbx-loc-tag ${TAG_CLASS[r.status]}`}>{r.label}</span>
                    </div>
                    <div className='sbx-big'>
                      <strong>{r.avg !== null ? fmtDecimal(r.avg) : '-'}</strong>
                      <span>acumulada · {fmtInt(r.stars.total)} reseñas</span>
                    </div>
                    <div className='sbx-stats'>
                      <div>
                        Nota del período
                        <strong>{r.curAvg !== null ? fmtDecimal(r.curAvg) : '-'}</strong>
                      </div>
                      <div>
                        Reseñas nuevas
                        <strong>{fmtInt(r.curN)}</strong>
                      </div>
                      <div>
                        Sin responder
                        <strong className={r.critOpen > 0 ? 'is-bad' : undefined}>{fmtInt(r.critOpen)}</strong>
                      </div>
                    </div>
                    <span className={`sbx-kpi-delta is-${d.tone}`}>{d.text}</span>
                    <p className='sbx-note'>{r.note}</p>
                    <button type='button' className='sbx-link' onClick={() => setSelectedId(r.id)}>
                      Ver el detalle de {names.get(r.id) ?? r.name}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className='sbx-table-note' style={{ borderTop: 'none', padding: 0 }}>
              La nota del período usa las reseñas de Google con estrellas dentro del rango elegido. Con menos de 5
              reseñas no es concluyente. Un crítico es una reseña sin respuesta registrada, con tono negativo o de 1 a
              2 estrellas, el mismo criterio de la Bandeja.
            </p>
          </Sect>
        </>
      )}
    </div>
  );
}
